import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import operationsService from '../../services/operationsService';
import toast from 'react-hot-toast';

export const fetchTasks = createAsyncThunk(
  'operations/fetchTasks',
  async (params, { rejectWithValue }) => {
    try {
      const { data } = await operationsService.getTasks(params);
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to fetch tasks');
    }
  }
);

export const fetchTask = createAsyncThunk(
  'operations/fetchTask',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await operationsService.getTask(id);
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to fetch task');
    }
  }
);

export const createTask = createAsyncThunk(
  'operations/createTask',
  async (taskData, { rejectWithValue }) => {
    try {
      const { data } = await operationsService.createTask(taskData);
      toast.success('Task created');
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to create task');
    }
  }
);

export const updateTaskStatus = createAsyncThunk(
  'operations/updateTaskStatus',
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const { data } = await operationsService.updateTaskStatus(id, status);
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to update task');
    }
  }
);

export const fetchDockSlots = createAsyncThunk(
  'operations/fetchDockSlots',
  async ({ warehouseId, params }, { rejectWithValue }) => {
    try {
      const { data } = await operationsService.getDockSlots(warehouseId, params);
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to fetch dock slots');
    }
  }
);

const initialState = {
  tasks: [],
  currentTask: null,
  taskTotalCount: 0,
  taskLoading: false,
  dockSlots: [],
  dockLoading: false,
  error: null,
};

const operationsSlice = createSlice({
  name: 'operations',
  initialState,
  reducers: {
    clearCurrentTask: (state) => { state.currentTask = null; },
    clearError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTasks.pending, (state) => { state.taskLoading = true; })
      .addCase(fetchTasks.fulfilled, (state, action) => {
        state.taskLoading = false;
        state.tasks = (() => { const _d = action.payload; return Array.isArray(_d?.results) ? _d.results : Array.isArray(_d?.items) ? _d.items : Array.isArray(_d) ? _d : []; })();
        state.taskTotalCount = action.payload.count || 0;
      })
      .addCase(fetchTasks.rejected, (state, action) => {
        state.taskLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchTask.fulfilled, (state, action) => {
        state.currentTask = action.payload;
      })
      .addCase(createTask.fulfilled, (state, action) => {
        state.tasks.unshift(action.payload);
      })
      .addCase(updateTaskStatus.fulfilled, (state, action) => {
        const idx = state.tasks.findIndex((t) => t.id === action.payload.id);
        if (idx !== -1) state.tasks[idx] = action.payload;
        if (state.currentTask?.id === action.payload.id) {
          state.currentTask = action.payload;
        }
      })
      .addCase(fetchDockSlots.pending, (state) => { state.dockLoading = true; })
      .addCase(fetchDockSlots.fulfilled, (state, action) => {
        state.dockLoading = false;
        state.dockSlots = (() => { const _d = action.payload; return Array.isArray(_d?.results) ? _d.results : Array.isArray(_d?.items) ? _d.items : Array.isArray(_d) ? _d : []; })();
      })
      .addCase(fetchDockSlots.rejected, (state) => { state.dockLoading = false; });
  },
});

export const { clearCurrentTask, clearError } = operationsSlice.actions;
export default operationsSlice.reducer;
